import type { NextResponse } from "next/server";
import { deleteCookie, setCookie } from "cookies-next";

/**
 * Auth cookies are host-only (no Domain attribute).
 *
 * Never set Domain to the apex/registrable domain (e.g. example.com).
 * Browsers treat that as covering every subdomain, so a production apex
 * login would be sent to all environments in the same browser.
 *
 * Host-only cookies are bound to the exact hostname that set them, so
 * each environment keeps its own session.
 */

const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1", "::1", "0.0.0.0"]);
const IPV4_PATTERN = /^\d{1,3}(?:\.\d{1,3}){3}$/;

export const AUTH_SESSION_COOKIE_KEYS = [
  "token",
  "ca_user_id",
  "company_id",
  "company_name",
  "company_user_id",
  "user_type",
  "user_type_id",
  "user_pay_type",
  "user_role",
  "user_role_id",
  "user_full_name",
  "first_name",
  "middle_name",
  "last_name",
  "password_expiry_dtm",
  "locked_out_flag",
  "email",
  "ca_user_active_flag",
  "company_user_active_flag",
  "user_company_association_flag",
  "sns_id",
  "message",
  "status",
  "session_timeout_minutes",
] as const;

const AUTH_EXTRA_COOKIE_KEYS = [
  "isAuthenticated",
  "user_avatar",
  "user_signature",
  "locked_email",
] as const;

const ALL_AUTH_COOKIE_KEYS: readonly string[] = [
  ...AUTH_SESSION_COOKIE_KEYS,
  ...AUTH_EXTRA_COOKIE_KEYS,
];

export type AuthCookieOptions = {
  path: string;
  sameSite: "lax";
  secure?: boolean;
  maxAge?: number;
  expires?: Date;
};

function resolveHostname(hostname?: string): string {
  return (
    hostname ??
    (typeof window !== "undefined" ? window.location.hostname : "")
  ).toLowerCase();
}

/**
 * Apex / registrable domain derived from the current hostname
 * (last two labels). Used only when clearing legacy Domain-scoped cookies.
 * Examples: dev.example.com → example.com; www.example.com → example.com.
 */
export function getParentCookieDomain(hostname?: string): string | undefined {
  const host = resolveHostname(hostname);

  if (!host || LOCAL_HOSTS.has(host) || IPV4_PATTERN.test(host)) {
    return undefined;
  }

  const parts = host.split(".").filter(Boolean);
  if (parts.length < 2) {
    return undefined;
  }

  return parts.slice(-2).join(".");
}

/**
 * @deprecated Domain must not be set for auth cookies. Kept so call sites
 * that still pass hostname keep compiling; always returns undefined.
 */
export function getCookieDomain(_hostname?: string): undefined {
  return undefined;
}

export function getAuthCookieOptions(
  _hostname?: string,
  isSecure?: boolean,
): AuthCookieOptions {
  const secure =
    isSecure ??
    (typeof window !== "undefined" && window.location.protocol === "https:");

  return {
    path: "/",
    sameSite: "lax",
    ...(secure ? { secure: true } : {}),
  };
}

function getKnownCookieDomainsToClear(hostname?: string): Array<string | undefined> {
  const host = resolveHostname(hostname);
  const parent = getParentCookieDomain(host);

  // undefined = host-only cookie (no Domain attribute)
  const domains: Array<string | undefined> = [undefined];

  if (parent) {
    domains.push(parent);
  }

  if (host && host !== parent) {
    domains.push(host);
  }

  return domains;
}

export function setAuthCookie(
  name: string,
  value: string | number | boolean,
  extra?: Pick<AuthCookieOptions, "maxAge" | "expires">,
): void {
  setCookie(name, String(value), { ...getAuthCookieOptions(), ...extra });
}

export function deleteAuthCookie(name: string, hostname?: string): void {
  const base = getAuthCookieOptions(hostname);
  for (const domain of getKnownCookieDomainsToClear(hostname)) {
    deleteCookie(name, domain ? { ...base, domain } : base);
  }
}

export function clearAuthCookies(hostname?: string): void {
  for (const key of ALL_AUTH_COOKIE_KEYS) {
    deleteAuthCookie(key, hostname);
  }
}

/**
 * Expire auth cookies on a NextResponse for every Domain variant we may
 * have written historically (host-only, parent apex, current hostname).
 */
export function clearAuthCookiesOnResponse(
  res: NextResponse,
  hostname?: string,
): NextResponse {
  const isProd = process.env.NODE_ENV === "production";
  const base = getAuthCookieOptions(hostname, isProd);

  for (const name of ALL_AUTH_COOKIE_KEYS) {
    for (const domain of getKnownCookieDomainsToClear(hostname)) {
      res.cookies.set({
        name,
        value: "",
        ...base,
        ...(domain ? { domain } : {}),
        maxAge: 0,
        expires: new Date(0),
      });
    }
  }

  return res;
}
